import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import {
	type CheckInAction,
	type CheckInActionId,
	checkInActionToLabelKey
} from '@/features/marquee/utils'
import type { MarqueeAttendee } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

interface CheckInInlineActionProps {
	attendee: MarqueeAttendee
	/**
	 * The one action the row promotes out of the kebab, already resolved against
	 * the event's state by `checkInRowControls`, or `null` when the row has
	 * nothing open. So this never offers a write the backend would refuse.
	 */
	action: CheckInAction | null
	onAction: (attendee: MarqueeAttendee, action: CheckInActionId) => void
	/** Which action is in flight for this row, so the spinner lands correctly. */
	pending: CheckInActionId | null
}

/**
 * The desktop row's one-click action, sitting in the action column beside the
 * kebab, which lists what is left: an action promoted to a button here is
 * dropped from the menu rather than offered twice a few pixels apart.
 */
export function CheckInInlineAction({
	attendee,
	action,
	onAction,
	pending
}: CheckInInlineActionProps) {
	const { t } = useTranslation()

	if (action === null) {
		return null
	}

	const isPending = pending === action.id

	return (
		<Button
			className='whitespace-nowrap'
			// The whole row locks, not just this button: the kebab's items answer
			// the same question, so a second write mid-flight would race the first.
			disabled={pending !== null}
			onClick={() => onAction(attendee, action.id)}
			size='sm'
			type='button'
			variant='secondaryOutline'
		>
			{t(checkInActionToLabelKey[action.id])}
			{isPending && <Spinner size={14} />}
		</Button>
	)
}
